'use client';

/**
 * CitationPill — inline mint-tinted monospace pill for Astor output references.
 *
 * Two kinds:
 *  - `source` — renders as `[T1]` / `[15]` style citations linking to a
 *    source row in the right-rail citations panel. Matches `pages/home-astor.jsx`
 *    AstorActive citation markers.
 *  - `tool`   — renders as `↳ credit.pull` for the tool-trace inventory in
 *    AstorBlock (`pages/loans.jsx` `tools.map`).
 *
 * Distinct from {@link CitationChip} (which is the existing InlineTagChip-based
 * citation tag) — this is the v3-design dedicated mint-pill rendering.
 */

import { Box } from '@mui/material';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { forwardRef, type MouseEvent, type ReactNode } from 'react';
import { useI18n } from '../../hooks';

export type CitationPillKind = 'source' | 'tool';

export interface CitationPillProps {
  /** Pill kind — drives whether the leading `↳` glyph and brackets are shown. */
  kind: CitationPillKind;
  /** Label inside the pill (e.g. `T1`, `15`, or `credit.pull`). */
  label: ReactNode;
  /** Optional click handler — promotes the pill to a focusable button. */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /** Optional aria-label override. */
  ariaLabel?: string;
  sx?: SxProps<Theme>;
}

/**
 * Inline mint-tinted mono pill.
 */
export const CitationPill = forwardRef<HTMLElement, CitationPillProps>(function CitationPill(
  { kind, label, onClick, ariaLabel, sx },
  ref,
) {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';

  const mint = theme.palette.success.main;
  const mintDim = alpha(mint, isDark ? 0.18 : 0.14);
  const mintBorder = alpha(mint, 0.38);

  const labelText = typeof label === 'string' ? label : '';
  const resolvedAria =
    ariaLabel ??
    (labelText
      ? kind === 'source'
        ? t('astor.citationPill.sourceAriaLabel', { label: labelText })
        : t('astor.citationPill.toolAriaLabel', { label: labelText })
      : undefined);

  const baseSx: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.375,
    lineHeight: 1,
    px: kind === 'tool' ? 0.875 : 0.625,
    py: 0.25,
    backgroundColor: mintDim,
    color: mint,
    border: `1px solid ${mintBorder}`,
    borderRadius: 0.5,
    fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
    fontSize: '0.625rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  };

  const interactiveSx: SxProps<Theme> = onClick
    ? {
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover': {
          backgroundColor: alpha(mint, isDark ? 0.24 : 0.2),
        },
        '&:focus-visible': {
          outline: `2px solid ${mint}`,
          outlineOffset: 2,
        },
      }
    : {};

  const content = (
    <>
      {kind === 'tool' && (
        <Box component="span" aria-hidden sx={{ fontFamily: 'inherit', fontSize: 'inherit', opacity: 0.85 }}>
          ↳
        </Box>
      )}
      <Typography
        component="span"
        sx={{ fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 1 }}
      >
        {label}
      </Typography>
    </>
  );

  if (onClick) {
    return (
      <Box
        ref={ref as React.Ref<HTMLButtonElement>}
        component="button"
        type="button"
        onClick={onClick}
        aria-label={resolvedAria}
        sx={[baseSx, interactiveSx, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      ref={ref as React.Ref<HTMLSpanElement>}
      component="span"
      role="note"
      aria-label={resolvedAria}
      sx={[baseSx, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {content}
    </Box>
  );
});

CitationPill.displayName = 'CitationPill';

export default CitationPill;
